import React, { useState } from 'react';

const LABEL = 'block text-[10px] uppercase tracking-chip text-gold-deep mb-2';
const INPUT =
  'w-full bg-transparent border-0 border-b border-gold/40 py-3 text-ink placeholder:text-gold-deep/50 focus:outline-none focus:border-rani transition-colors rounded-none';

const INITIAL = {
  name: '',
  phone: '',
  email: '',
  side: '',
  attending: '',
  guests: '1',
  arrival: '20 November (Friday)',
  message: '',
};

// Phone input: keep digits plus spaces, dashes and brackets; allow one "+" only at the start
const cleanPhone = (v) => {
  const plus = v.trimStart().startsWith('+') ? '+' : '';
  return plus + v.replace(/[^\d\s()-]/g, '');
};

const RSVP = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  const accepting = form.attending === 'Joyfully accepts';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    if (!form.name.trim()) return fail('Please tell us your name.');
    const digits = form.phone.replace(/\D/g, '').length;
    if (digits < 8 || digits > 15) return fail('Please enter a valid phone number (8 to 15 digits). Add your country code if you’re outside India, e.g. +81 for Japan.');
    if (!form.side) return fail('Please tell us if you’re from the bride’s side or the groom’s side.');
    if (!form.attending) return fail('Please let us know if you can join us.');
    if (accepting && !form.arrival) return fail('Please tell us which day you’ll arrive at the resort.');
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) return fail('That email address doesn’t look right.');

    const url = import.meta.env.VITE_RSVP_URL;
    if (!url) {
      console.warn('VITE_RSVP_URL is not set; RSVP not sent.');
      return fail("RSVP isn't connected yet. Please reach out to us directly for now.");
    }

    setError('');
    setStatus('sending');
    try {
      await fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          side: form.side,
          attending: form.attending,
          guests: accepting ? form.guests : '',
          arrival: accepting ? form.arrival : '',
          message: form.message.trim(),
          submittedAt: new Date().toISOString(),
        }),
      });
      setStatus('success');
    } catch (err) {
      console.warn('RSVP submit failed', err);
      fail('Something went wrong sending your RSVP. Please try again.');
    }
  };

  function fail(msg) {
    setError(msg);
    setStatus('error');
  }

  const toggleBtn = (value, label, field = 'attending') => {
    const active = form[field] === value;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setForm((f) => ({ ...f, [field]: value }))}
        className={`flex-1 py-3 px-4 border text-xs uppercase tracking-chip transition-colors ${
          active
            ? 'bg-rani border-rani text-ivory'
            : 'border-gold/40 text-gold-deep hover:border-rani hover:text-rani'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <section className="rsvp relative z-10 bg-ivory py-24 px-6 md:py-32" id="rsvp">
      <div className="inner max-w-2xl mx-auto border border-gold/40 bg-bone/40 p-6 sm:p-10 md:p-14">
        {status === 'success' ? (
          <div className="text-center py-10" role="status">
            <h2 className="font-display text-4xl md:text-5xl text-rani mb-4">Dhanyawaad!</h2>
            <p className="font-display text-xl text-ink">We can't wait to celebrate with you.</p>
            {form.attending === 'Regretfully declines' && (
              <p className="font-display text-xl text-ink mt-3">We'll miss you. Thank you for your blessings.</p>
            )}
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="text-[10px] uppercase tracking-chip text-gold-deep mb-4">RSVP</div>
              <h2 className="font-display text-4xl md:text-5xl text-rani mb-5">
                Come <em className="italic">celebrate</em> with us.
              </h2>
            </div>

            <form className="space-y-7" id="rsvpForm" onSubmit={submit} noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <div>
                  <label className={LABEL} htmlFor="rsvp-name">Full name *</label>
                  <input id="rsvp-name" className={INPUT} type="text" autoComplete="name" value={form.name} onChange={set('name')} required />
                </div>
                <div>
                  <label className={LABEL} htmlFor="rsvp-phone">Phone / WhatsApp *</label>
                  <input
                    id="rsvp-phone"
                    className={INPUT}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+91 98765 43210"
                    maxLength={20}
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: cleanPhone(e.target.value) }))}
                    required
                  />
                  <p className="mt-1.5 text-[11px] text-ink/60">Include your country code if you’re outside India, e.g. +81 for Japan.</p>
                </div>
              </div>

              <div>
                <label className={LABEL} htmlFor="rsvp-email">Email (optional)</label>
                <input id="rsvp-email" className={INPUT} type="email" autoComplete="email" value={form.email} onChange={set('email')} />
              </div>

              <div>
                <span className={LABEL}>You are a guest from *</span>
                <div className="flex flex-col sm:flex-row gap-3">
                  {toggleBtn("Bride's side", "Bride's side · Sonakshi", 'side')}
                  {toggleBtn("Groom's side", "Groom's side · Shubham", 'side')}
                </div>
              </div>

              <div>
                <span className={LABEL}>Will you be there? *</span>
                <div className="flex flex-col sm:flex-row gap-3">
                  {toggleBtn('Joyfully accepts', 'Joyfully accepts')}
                  {toggleBtn('Regretfully declines', 'Regretfully declines')}
                </div>
              </div>

              {accepting && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div>
                      <label className={LABEL} htmlFor="rsvp-guests">Number of guests including you</label>
                      <select id="rsvp-guests" className={INPUT} value={form.guests} onChange={set('guests')}>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="rsvp-arrival">When will you arrive at the resort? *</label>
                      <select id="rsvp-arrival" className={INPUT} value={form.arrival} onChange={set('arrival')}>
                        <option value="20 November (Friday)">20 November · Friday</option>
                        <option value="21 November (Saturday)">21 November · Saturday</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className={LABEL} htmlFor="rsvp-message">Message for the couple</label>
                <textarea id="rsvp-message" className={`${INPUT} min-h-[100px] resize-y`} value={form.message} onChange={set('message')} />
              </div>

              {error && (
                <p className="text-rani text-sm" role="alert">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-rani text-ivory py-4 px-8 uppercase tracking-chip text-xs hover:bg-rani-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? 'Sending…' : 'Send RSVP →'}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

export default RSVP;
